<template>
  <li v-if="!subscription.hide_paused">
    <span>{{ productName }}</span>
    <span>
      <template v-if="isActive">
        <span><strong>Valid</strong></span>
        : {{ formatDate(subscription.start_date) }} -
        {{ formatDate(subscription.end_date) }}
      </template>
      <template v-else-if="isPaused">
        <span><strong>Paused Until</strong></span>
        : {{ formatDate(subscription.start_date) }}
      </template>
      <template v-else-if="isExpired">
        <span><strong>Expired</strong></span>
        : {{ formatDate(subscription.end_date) }}
      </template>
      <template v-if="subscription.unsubscribed_at">
        <br />
        <span>
          <small class="unsubscribed_notice">
            On {{ formatDate(subscription.unsubscribed_at) }} you unsubscribed.
          </small>
        </span>
        <span>
          <small class="unsubscribed_notice">
            Your subscription will not auto renew.
          </small>
        </span>
      </template>
    </span>
    <span class="button-container">
      <template v-if="subscription.isUpgradeable">
        <span class="action-text" @click="showUpgradeOptions()">UPGRADE</span>
      </template>
      <a :href="viewExpiredButtonLink" :class="getViewExpiredButtonClass">
        {{ viewExpiredButtonText }}
      </a>
      <a
        @click="subscribeUnsubscribeButtonLink()"
        :class="getSubscribeUnsubscribeButtonClass"
      >
        {{ subscribeUnsubscribeButtonText }}
      </a>
    </span>
  </li>
  <BaseModal
    v-if="isShowing"
    @background-clicked="handleBackgroundClicked()"
    rounded
    dropShadow
    size="md"
    :noPadding="true"
  >
    <div class="subscription-modal">
      <h4 class="subscription-modal__title">
        Thanks for your interest in upgrading your
        {{ productName }} subscription!
      </h4>
      <p class="subscription-modal__subtitle">
        Which {{ productName }} tier are you interested in learning more about?
      </p>
      <div class="subscription-modal__button-container">
        <a
          v-for="product in upgradeOptions"
          class="button subscription-modal__button"
          :href="`/store/product/${product.url}`"
        >
          {{ product.name }}
        </a>
      </div>
    </div>
  </BaseModal>
</template>

<script>
  import BaseModal from '~/components/base/BaseModal.vue';

  export default {
    components: {
      BaseModal,
    },
    props: {
      subscription: {
        type: Object,
        required: true,
      },
      user: {
        type: Object,
        required: true,
      },
      bundles: {
        type: Array,
        required: true,
      },
      subscriptionIds: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        today: new Date(),
        isShowing: false,
      };
    },
    computed: {
      startDate() {
        return new Date(this.subscription.start_date);
      },
      endDate() {
        return new Date(this.subscription.end_date);
      },
      isFreeTrial() {
        return this.subscription.product.url.includes('free-trial');
      },
      freeTrialProductName() {
        return (
          this.subscription &&
          this.subscription.product.url.replace(/-free-trial/g, '')
        );
      },
      productName() {
        return this.subscription.product.name == 'TrendsReport'
          ? 'Trends Report'
          : this.subscription.product.name;
      },
      isActive() {
        return (
          this.subscription.active &&
          (this.startDate <= this.today || this.startDate === this.today)
        );
      },
      isPaused() {
        return !this.subscription.active && this.startDate > this.today;
      },
      isExpired() {
        return this.endDate < this.today;
      },
      viewExpiredButtonText() {
        const isDatacast = [
          'datacast-essentials',
          'datacast',
          'datacast-essentials-annual',
        ].includes(this.freeTrialProductName);
        const isInsider = this.freeTrialProductName === 'insider';
        if (
          (isDatacast || isInsider) &&
          !this.subscription.active &&
          this.isFreeTrial
        ) {
          return 'Expired';
        } else {
          return 'View';
        }
      },
      viewExpiredButtonLink() {
        if (this.subscription.product.bundle) {
          return `/store/product/${this.subscription.product.url}`;
        }
        if (this.subscription.product.url.includes('datacast')) {
          return '/datacast';
        }
        if (this.subscription.product.url.includes('insider')) {
          return '/insider';
        }
        if (this.subscription.product.url.includes('by-the-numbers')) {
          return '/by-the-numbers';
        }
        return `/${this.subscription.product.url}`;
      },
      subscribeUnsubscribeButtonText() {
        const insiderAnnual =
          this.subscription.product.url === 'insider-annual';
        const isFreeTrial = [
          'datacast-essentials-free-trial',
          'datacast-free-trial',
          'insider-free-trial',
        ].includes(this.subscription.product.url);
        if (
          !this.subscription.active &&
          this.startDate > this.today &&
          !insiderAnnual
        ) {
          // do nothing
        } else if (
          this.subscription.unsubscribed_at === null &&
          this.subscription.active &&
          !insiderAnnual
        ) {
          return 'Unsubscribe';
        } else if (
          !this.subscription.active &&
          this.startDate <= this.today &&
          !insiderAnnual
        ) {
          if (!isFreeTrial) {
            return 'Subscribe';
          }
        } else if (
          this.subscription.unsubscribed_at === null &&
          insiderAnnual
        ) {
          return 'Unsubscribe';
        }
        return 'Contact Us';
      },
      getViewExpiredButtonClass() {
        return this.viewExpiredButtonText === 'View'
          ? 'button-green'
          : 'button-grey';
      },
      getSubscribeUnsubscribeButtonClass() {
        return this.subscribeUnsubscribeButtonText === 'Subscribe'
          ? 'button-blue more-padding'
          : 'button-grey';
      },
      upgradeOptions() {
        return this.bundles.filter((product) => {
          return (
            this.subscription.upgradeOptions.includes(product.id) &&
            !this.subscriptionIds.includes(product.id)
          );
        });
      },
    },
    methods: {
      formatDate(date) {
        const dateArray = date.split('-');
        const year = dateArray[0];
        const month = dateArray[1].padStart(2, '0');
        const day = dateArray[2].padStart(2, '0');
        return `${month}/${day}/${year}`;
      },
      async subscribeUnsubscribeButtonLink() {
        if (this.subscribeUnsubscribeButtonText === 'Contact Us') {
          window.location = '/contact';
          return;
        }

        if (this.subscribeUnsubscribeButtonText === 'Subscribe') {
          window.location = `/store/product/${this.subscription.product.url}`;
          return;
        }

        const isByTheNumbersMonthly =
          this.subscription.product.url === 'by-the-numbers';

        if (isByTheNumbersMonthly) {
          window.location = `/dashboard/unsubscribe/before-you-go/${this.subscription.product.url}`;
          return;
        }

        if (
          this.subscription.product.name === 'Insider Plus' ||
          this.subscription.product.name === 'Insider Plus Annual' ||
          this.subscription.product.name === 'Insider Suite'
        ) {
          window.location = `/dashboard/unsubscribe/${this.subscription.product.url}`;
          return;
        }

        if (
          this.subscription.unsubscribed_at === null &&
          this.subscription.active
        ) {
          if (
            this.subscription.product.name === 'Insider Annual' ||
            this.subscription.product.url === 'by-the-numbers-annual'
          ) {
            const formData = new FormData();
            formData.append('product', this.subscription.product.url);
            try {
              await axios.post(
                `/dashboard/unsubscribe/${this.subscription.product.url}`,
                formData
              );
              window.location = `/dashboard/unsubscribe/${this.subscription.product.url}?result=success`;
            } catch (error) {
              window.location = `/dashboard/unsubscribe/${this.subscription.product.url}?result=failure`;
            }
          } else {
            window.location = `/dashboard/unsubscribe/${this.subscription.product.url}`;
          }
        }
      },
      showUpgradeOptions() {
        this.isShowing = true;
      },
      handleBackgroundClicked() {
        this.isShowing = false;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .button-container {
    display: unset;
    @include screen-md {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      width: 33%;
      flex-wrap: wrap;
    }
  }
  .action-text {
    font-size: 25px !important;
    font-style: italic !important;
    font-weight: 700;
    text-decoration: underline !important;
    color: var(--itr-blue) !important;
    margin: 2px 20px 3px 0;
    cursor: pointer;
  }
  .button-green,
  .button-blue,
  .button-grey {
    margin: 3px 0;
  }

  .more-padding {
    padding: 8px 33px;
  }

  .subscription-modal {
    padding: 32px;
    text-align: center;
    margin: auto;
    &__title {
      margin-bottom: 2rem;
      font-weight: 700;
      color: var(--itr-blue);
      @include screen-md {
        font-size: 30px;
      }
    }
    &__subtitle {
      margin-bottom: 1.5rem;
      font-size: 18px;
      font-weight: 700;
      color: var(--itr-charcoal);
    }
    &__button-container {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
    }
    &__button {
      width: 228px;
      padding: 4px 40px !important;
      margin-top: 1.1rem;
    }
  }
</style>
